import { COMMENT_TAGS } from '@forecastify/shared';
import { PROSE } from '@forecastify/ui';
import type { Element, ElementContent, Root, RootContent, Text } from 'hast';
import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { useWorkspacePath } from '../../workspace-path';
import Markdown, { type Components } from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { KEYS, LINK, keyHref } from './agent-prose';

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ C48 · the model writes markdown, and the thread renders it. R-33         │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * Reported: *"if the LLM responds with styled texts, even with tables and such in the prose, we
 * should display them correctly."* Until now the preamble's `VOICE` told the model the opposite —
 * *"do not use markdown formatting: the thread renders plain text, so asterisks and backticks arrive
 * as asterisks and backticks"* — which was true, and which is the sentence this file replaces.
 *
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ ⚠️⚠️ THE PROMPT IS THE COUPLED HALF, AND SHIPPING ONE OF THE TWO IS A     │
 * │ WELL-FORMED BUILD THAT DOES NOTHING.                                      │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * Renderer without prompt: the model keeps writing plain text and nothing changes. Prompt without
 * renderer: every reply fills with asterisks. R-26 already cost this project that lesson once, with
 * the data-cards paragraph — so `preamble.ts`'s `VOICE` states the subset it may use, this file
 * states what it renders, both point at each other, and a test on each side asserts its own half.
 *
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ ⚠️⚠️ A MODEL-AUTHORED TABLE IS NOT THE PRODUCT'S TABLE, AND R-26 IS THE   │
 * │ REASON THAT MATTERS.                                                      │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * *"If the model authors a markdown table … then the model is the source of the rows — it will drop a
 * column, round an hour, invent a status name, or summarise five rows as four."* That argument was
 * for a table INSTEAD OF the tool's own `data` frame, and it still holds against one. What changed is
 * the ground under it: since C27.7 the data cards are off, so the model already authors every list as
 * prose — one line per row, because the preamble asks it to. A grid does not make those rows less
 * trustworthy than the lines they already are. It makes them **look** more authoritative.
 *
 * So the table renders, and it renders as a **hairline grid** (`PROSE`) rather than through `DataList`
 * — deliberately not the component the tasks screen uses, so a reader cannot mistake the model's
 * summary for rows a service returned. R-33 records that the fabrication argument was weighed rather
 * than forgotten, and that turning the data cards back on remains the answer if the distinction ever
 * stops being visible enough.
 *
 * ⚠️ **Only the model's text comes through here.** The person's own message keeps `AgentProse` and
 * `whitespace-pre-line`: somebody who typed `2 * 3 * 4` wrote arithmetic, not an italic run, and
 * parsing their message as markup would silently eat characters they typed.
 */

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ ⚠️⚠️ WHAT MAY RENDER, AND WHY IT STARTS FROM THE COMMENT LIST.            │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * `COMMENT_TAGS` is what a comment may contain — the list the server's sanitiser and TipTap's toolbar
 * both read (R-16). Starting from it means the Agent's bold, code, lists and quotes are the same set
 * of tags the rest of the product's rich text uses, and `PROSE` gives them the same look. A third
 * vocabulary for the chat is how *"formatted"* comes to mean something different on each screen.
 *
 * ⚠️ **`#text` is dropped**, because it is DOMPurify's way of saying *"keep the words"* and means
 * nothing to an element allow-list.
 *
 * ⚠️⚠️ **THE ADDITIONS ARE ADDED HERE, NOT TO `COMMENT_TAGS`.** Widening the shared list would widen
 * what the server stores in a comment body, which is a sanitiser change with a security story
 * attached — and no comment can produce a table anyway. The Agent's markdown is **rendered and never
 * stored**: it is re-parsed from the model's own text on every load, so an allow-list here is a
 * display decision, not a persistence one.
 *
 * ⚠️ **`h1` is allowed only to be renamed.** It is absent from `COMMENT_TAGS` because a body inside a
 * page that already has a document heading must not add a second one — but a model that writes `#`
 * anyway should get a heading rather than a stray paragraph, so it is admitted and rendered as `h2`.
 * `h4`–`h6` are not: they are unwrapped to their own text, which is what a heading that deep is.
 *
 * ⚠️ **`img` and `hr` stay out**, for the reasons `comment-markup.ts` gives, and one more: an `img`
 * from a model is an arbitrary host named in a reply.
 */
const ELEMENTS: readonly string[] = [
  ...COMMENT_TAGS.filter((tag) => tag !== '#text'),
  'h1',
  /* GFM strikethrough. `COMMENT_TAGS` has `s`; `remark-gfm` emits `del`. */
  'del',
  'table',
  'thead',
  'tbody',
  'tr',
  'th',
  'td',
];

/** Text inside these is text, not markup: a key in a code span is a code span. */
const OPAQUE = new Set(['a', 'code', 'pre']);

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ ⚠️⚠️ THE KEY LINKS SURVIVE BECAUSE THEY MOVED INSIDE THE TREE.            │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * `AgentProse` linkified a flat string. Once markdown parses first, a regex over the source would be
 * a regex over syntax — and running it after render is not possible at all. So the pass runs on the
 * parsed tree, which fixes the thing that was already broken: a key inside `**HSO-131**` used to keep
 * its asterisks AND its link; a key inside a list item or a table cell now gets one too.
 *
 * ⚠️ **`code`, `pre` and `a` are opaque.** A key in a code span is being shown, not referenced, and
 * turning it into a link would put an anchor inside an anchor for the one case where the model wrote
 * a link itself.
 *
 * ⚠️ **Splices in place and steps the index past what it inserted.** The alternative — rebuilding
 * every children array — allocates for the majority of nodes that contain no key at all.
 */
function rehypeKeys() {
  return (tree: Root): void => {
    linkKeys(tree.children);
  };
}

function linkKeys(nodes: (RootContent | ElementContent)[]): void {
  for (let index = 0; index < nodes.length; index += 1) {
    const node = nodes[index];

    if (node === undefined) continue;

    if (node.type === 'element') {
      if (!OPAQUE.has(node.tagName)) linkKeys(node.children);
      continue;
    }

    if (node.type !== 'text') continue;

    const split = splitKeys(node.value);

    if (split === null) continue;

    nodes.splice(index, 1, ...split);
    index += split.length - 1;
  }
}

/** One text node into text-and-anchors, or `null` when it holds no identifier. */
function splitKeys(text: string): (Element | Text)[] | null {
  const parts: (Element | Text)[] = [];
  let cursor = 0;

  for (const match of text.matchAll(KEYS)) {
    const href = keyHref(match);

    /* ⚠️ Unreachable, and left as a skip rather than an assertion — same argument as `keyHref`. */
    if (href === null) continue;

    const at = match.index;

    if (at > cursor) parts.push({ type: 'text', value: text.slice(cursor, at) });
    cursor = at + match[0].length;

    parts.push({
      type: 'element',
      tagName: 'a',
      /* ⚠️ The marker is what separates OUR anchor from one the model wrote — see `components.a`.
       * Not the `href`'s shape: a model can write an internal path too, and the distinction that
       * matters is who authored the link, not where it points. */
      properties: { href, dataAgentKey: 'true' },
      children: [{ type: 'text', value: match[0] }],
    });
  }

  if (parts.length === 0) return null;
  if (cursor < text.length) parts.push({ type: 'text', value: text.slice(cursor) });

  return parts;
}

/**
 * ⚠️⚠️ **RAW HTML BECOMES TEXT RATHER THAN DISAPPEARING.** `react-markdown` renders no raw HTML
 * without `rehype-raw`, which is the property that makes this safe — but its default is to DROP the
 * node, and a dropped `html` node takes the words inside it with it. So `<b>fontos</b>` in a reply
 * would render as nothing at all, which reads as the Agent having answered with a hole.
 *
 * Turning it into text keeps every character and executes none of it: a reply containing a `<script>`
 * tag shows the tag. ⚠️ **`rehype-raw` must never be added here** — the model's text quotes workspace
 * text written by people, and that is the whole reason nothing in this thread trusts markup.
 */
function remarkHtmlAsText() {
  return (tree: { children?: unknown[] }): void => {
    textify(tree);
  };
}

function textify(node: unknown): void {
  if (typeof node !== 'object' || node === null) return;

  const branch = node as { type?: string; children?: unknown[] };

  if (branch.children === undefined) return;

  for (const child of branch.children) {
    if (typeof child !== 'object' || child === null) continue;

    const leaf = child as { type?: string };

    if (leaf.type === 'html') leaf.type = 'text';
    else textify(child);
  }
}

/**
 * One of our own key anchors, with the workspace segment applied.
 *
 * ⚠️⚠️ **A COMPONENT RATHER THAN AN INLINE `<Link>`, BECAUSE `COMPONENTS` IS A MODULE CONSTANT.**
 * R-38 makes the address `/{workspace}/tasks/HSO-131`, and the workspace comes from
 * `useWorkspacePath()` — a hook, which a module-level object literal cannot call. Rebuilding
 * `COMPONENTS` per render inside `AgentMarkdown` would work and would put a fresh `components`
 * object into `react-markdown` on every streamed token; a leaf component reads the hook where a
 * hook is allowed and leaves the constant alone.
 *
 * ⚠️ `rehypeKeys` sets `data-agent-key` on OUR anchors only, so this is never reached for a link
 * the model wrote — those lose their destination and keep their words, which the note on
 * `COMPONENTS.a` argues at length.
 */
function AgentKeyLink({
  href,
  children,
}: {
  readonly href: string;
  readonly children: ReactNode;
}): ReactNode {
  const at = useWorkspacePath();
  return (
    <Link to={at(href)} className={LINK}>
      {children}
    </Link>
  );
}

const COMPONENTS: Components = {
  /**
   * ⚠️⚠️ **A LINK THE MODEL WROTE LOSES ITS DESTINATION AND KEEPS ITS WORDS.** `agent-prose.tsx`
   * argues why at length, and markdown does not change it: the reply quotes task titles, notes and
   * comments written by people, so a task named `https://evil.example/x` would otherwise become an
   * outbound click rendered in the product's own voice. `remark-gfm` autolinks bare URLs too, and
   * this is what makes that harmless.
   *
   * ⚠️ **Our own anchors are the ones carrying the marker**, and they go through `Link` so a key
   * navigates inside the app instead of reloading it.
   */
  a({ href, children, ...rest }) {
    if ('data-agent-key' in rest && href !== undefined) {
      return <AgentKeyLink href={href}>{children}</AgentKeyLink>;
    }

    return <>{children}</>;
  },
  /** ⚠️ A `#` heading renders as the `h2` the rest of the product's rich text tops out at. */
  h1: ({ children }) => <h2>{children}</h2>,
  /**
   * ⚠️⚠️ **THE TABLE SCROLLS INSIDE ITS OWN BOX**, because the reading column is 680px and a table
   * with five columns of the model's own making is not. Without this the page body scrolls sideways,
   * which CLAUDE.md's frontend section forbids outright.
   */
  table: ({ children }) => (
    <div className="overflow-x-auto">
      <table>{children}</table>
    </div>
  ),
};

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ ⚠️⚠️ `whitespace-pre-line` ON THE PARAGRAPHS, WHICH IS NOT A LEFTOVER.    │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * A single newline is a *soft break* in markdown: `a\nb` is one paragraph, and CommonMark renders it
 * as `a b`. The preamble asks for a list as *"one short line per row"* — twelve rows separated by one
 * newline each — and every conversation already stored was written under that instruction. Parsing
 * without this turns all of them into one run-on sentence, which is a regression the moment it ships.
 *
 * The newline is still a character in the text node, so `pre-line` renders it as the break the writer
 * meant. It is what GitHub's own comment renderer does, and it costs no dependency.
 *
 * ⚠️ **`[&>*+*]:mt-2.5` rather than a margin on `p`**, because Tailwind's reset removes block margins
 * and the gap belongs BETWEEN top-level blocks — a paragraph, a list and a table each need air around
 * them, and a `p` inside a tight list item does not.
 */
const REPLY = `${PROSE} [&>*+*]:mt-2.5 [&_li]:whitespace-pre-line [&_p]:whitespace-pre-line`;

/**
 * The model's own text, rendered.
 *
 * ⚠️ **Re-parsed on every `text_delta`, and this has NOT been profiled.** `agent-route.tsx` appends
 * each frame onto `turn.text`, so a streaming reply is parsed once per token over a growing string —
 * quadratic in the length of the reply. The reason it is shipped rather than solved is that the
 * number being squared is small: a reply is kilobytes. ⚠️ But it is the first place to look if the
 * thread ever feels heavy while a long answer arrives, and the fix is to **throttle the deltas**, not
 * to memoise — the string changes on every render by definition, so a `useMemo` on it caches nothing.
 *
 * ⚠️ **Half-arrived syntax is left to settle on its own.** Mid-stream, an unclosed `**` shows its
 * asterisks and an unterminated fence renders as a growing code block; both resolve when the closing
 * characters arrive. Holding a block back until it parses would mean the reader watching a reply
 * arrive sees less of it than the reader who opens it afterwards.
 */
export function AgentMarkdown({ text }: { readonly text: string }): ReactNode {
  return (
    <div className={REPLY}>
      <Markdown
        remarkPlugins={[remarkGfm, remarkHtmlAsText]}
        rehypePlugins={[rehypeKeys]}
        allowedElements={[...ELEMENTS]}
        /* ⚠️ A disallowed element keeps its words: `#### deep` is a paragraph, not a deletion. */
        unwrapDisallowed
        components={COMPONENTS}
      >
        {text}
      </Markdown>
    </div>
  );
}
